import { createContext, useContext, useEffect, useRef, useState } from 'react';

/**
 * "This page was hidden / shown again" signals for pages kept alive by KeepAlivePages.
 *
 * A kept page is NOT unmounted when the user leaves and NOT remounted on Back, so code that
 * runs "on mount" does not run again, and timers/effects started on it keep running while the
 * page is hidden. Where the old unmount/remount behavior is required — analytics events that
 * must fire exactly as before (docs/performance.md §۳.۵) — use these hooks.
 *
 * Deliberately a subscription with a stable identity, not React state at the page root: a
 * state change there would re-render the whole kept page and cancel the point of keeping it.
 * `useKeepAliveActive` does hold state, but only inside the (small) component that uses it.
 */

export type KeepAliveSignal = 'shown' | 'hidden';

export type KeepAliveShownEmitter = {
  subscribe: (listener: (signal: KeepAliveSignal) => void) => () => void;
  emit: (signal?: KeepAliveSignal) => void;
};

export function createKeepAliveShownEmitter(): KeepAliveShownEmitter {
  const listeners = new Set<(signal: KeepAliveSignal) => void>();
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    emit(signal = 'shown') {
      listeners.forEach(listener => {
        try {
          listener(signal);
        } catch {
          // one failing listener must not stop the others
        }
      });
    },
  };
}

export const KeepAliveShownContext = createContext<KeepAliveShownEmitter | null>(null);

/**
 * Calls `onShown` every time the surrounding kept page is shown again (Back to the same
 * URL). Never called on the first mount, and a no-op outside a kept page.
 */
export function useKeepAliveShown(onShown: () => void) {
  const emitter = useContext(KeepAliveShownContext);
  const latest = useRef(onShown);
  latest.current = onShown;

  useEffect(() => emitter?.subscribe(signal => signal === 'shown' && latest.current()), [emitter]);
}

/**
 * `false` while the surrounding kept page is hidden, `true` otherwise (always `true` outside a
 * kept page). Put it in an effect's dependencies and return early when it is `false`: the
 * effect then cleans up when the page is left and runs again on Back — exactly what an
 * unmount + remount did before keep-alive.
 */
export function useKeepAliveActive(): boolean {
  const emitter = useContext(KeepAliveShownContext);
  const [active, setActive] = useState(true);

  useEffect(() => emitter?.subscribe(signal => setActive(signal === 'shown')), [emitter]);
  return active;
}
